import Constants, { ItemTableConsts, LogicalOperators, MdmVendorStates } from 'client/util/Constants';
import DateUtil from 'client/util/DateUtil';

const {
  internal,
  external,
} = MdmVendorStates;

const {
  iOS,
  windows,
} = Constants;

const {
  linux,
  darwin,
  ios,
} = ItemTableConsts.parentOSType;

const formatDisplayValue = (str) => {
  if (str === ios) {
    return iOS;
  }

  if (str === darwin) {
    return 'Mac';
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const SystemFilters = {
  acknowledged: bool => ({
    name: 'acknowledged',
    operator: 'eq',
    value: bool,
  }),
  agentDoesNotMatchVersion: version => ({
    name: 'agentVersion',
    operator: '$ne',
    value: version,
  }),
  createdDaysAgo: (days, logicalOperator) => (
    {
      name: 'created',
      logicalOperator,
      operator: '$lte',
      value: DateUtil.formatDate(DateUtil.dateDaysAgo(days)),
    }
  ),
  createdOnOrBefore: date => ({
    name: 'created',
    field: 'created',
    operator: '$lte',
    value: date.format('YYYY-MM-DD'),
    type: 'status',
  }),
  distributionName: (name) => {
    const operator = 'eq';
    const value = name;

    return {
      name: 'osVersionDetail.distributionName',
      operator,
      value,
      type: 'OS',
      displayValue: value,
    };
  },
  fde: bool => ({
    name: 'fde.active',
    operator: 'eq',
    value: bool,
    type: 'disk status',
    displayValue: bool ? 'encrypted' : 'unencrypted',
  }),
  hasSecureToken: (bool, logicalOperator) => (
    {
      name: 'serviceAccountState.hasSecureToken',
      logicalOperator,
      operator: 'eq',
      value: bool,
      displayValue: 'hasSecureToken',
      type: 'secureToken',
    }),
  hasServiceAccount: (bool, logicalOperator) => (
    {
      name: 'hasServiceAccount',
      logicalOperator,
      operator: 'eq',
      value: bool,
      displayValue: 'install',
      type: 'agent',
    }),
  inactiveDays: (days) => (
    {
      name: 'lastContact',
      operator: '$lte',
      value: DateUtil.formatDate(DateUtil.dateDaysAgo(days)),
    }),
  lastActiveDays: (days) => {
    const operator = '$gt';
    const res = new Date();
    const value = new Date(res.setDate(res.getDate() - days)).toISOString().slice(0, 10);

    return {
      name: 'lastContact',
      operator,
      value,
      type: 'lastContact',
      displayValue: `${days} days`,
    };
  },
  passwordAPFSValid: (bool, logicalOperator) => (
    {
      name: 'serviceAccountState.passwordAPFSValid',
      logicalOperator,
      operator: 'eq',
      value: bool,
      displayValue: 'passwordAPFSValid',
      type: 'APFS',
    }),
  passwordODValid: (bool, logicalOperator) => (
    {
      name: 'serviceAccountState.passwordODValid',
      logicalOperator,
      operator: 'eq',
      value: bool,
      displayValue: 'passwordODValid',
      type: 'OD',
    }),
  mdm: (vendor, logicalOperator) => {
    let operator = 'eq';
    let displayValue = 'not enrolled';
    let vendorSearch = vendor;

    if (!vendor) {
      operator = '$ne';
      vendorSearch = internal;
    } else if (vendor === internal) {
      displayValue = 'enrolled with JumpCloud';
    } else if (vendor === external) {
      displayValue = 'enrolled with another vendor';
    }

    const filterObject = {
      name: 'mdm.vendor',
      operator,
      value: vendorSearch,
      type: 'MDM status',
      displayValue,
    };

    if (logicalOperator) {
      filterObject.logicalOperator = logicalOperator;
    }

    return filterObject;
  },
  mdmEnrollmentType: (type, logicalOperator, duplicate) => ({
    duplicate,
    name: 'mdm.enrollmentType',
    logicalOperator,
    operator: 'eq',
    value: type,
  }),
  mfa: bool => ({
    name: 'allowMultiFactorAuthentication',
    operator: 'eq',
    value: bool,
    type: 'MFA status',
    displayValue: bool ? 'active' : 'inactive',
  }),
  os: ({
    os, operator, logicalOperator, duplicate,
  }) => {
    let op = operator || 'eq';
    let value = os;

    if (os === windows) {
      op = '$regex';
      value = `/${os}/i`;
    }

    const filterObject = {
      name: 'os',
      operator: op,
      value,
      type: 'OS',
      displayValue: os,
    };
    if (logicalOperator) {
      filterObject.logicalOperator = logicalOperator;
    }

    if (duplicate) {
      filterObject.duplicate = duplicate;
    }
    return filterObject;
  },
  osFamily: (os, logicalOperator) => {
    let operator = 'eq';
    let value = os;

    if (os === linux) {
      operator = '$regex';
      value = '/linux|unknown/';
    }

    const filterObject = {
      name: 'osFamily',
      operator,
      value,
      type: 'OS',
      displayValue: formatDisplayValue(os),
    };

    if (logicalOperator) {
      filterObject.logicalOperator = logicalOperator;
    }

    return filterObject;
  },
  osName: (os) => {
    let operator = 'eq';
    let value = os;

    if (os === linux) {
      operator = '$regex';
      value = '/linux|unknown/';
    }

    return {
      name: 'osVersionDetail.osName',
      operator,
      value,
      type: 'OS',
      displayValue: formatDisplayValue(os),
    };
  },
  osMajor: (version) => {
    const operator = 'eq';
    const value = version;

    return {
      name: 'osVersionDetail.major',
      operator,
      value,
      type: 'Major',
      displayValue: value,
    };
  },
  osMinor: (version) => {
    const operator = 'eq';
    const value = version;

    return {
      name: 'osVersionDetail.minor',
      operator,
      value,
      type: 'Minor',
      displayValue: value,
    };
  },
  osRelease: (release) => {
    const operator = 'eq';
    const value = release;

    return {
      name: 'osVersionDetail.releaseName',
      operator,
      value,
      type: 'Release',
      displayValue: value,
    };
  },
  osVersion: (version) => {
    const operator = '$gte';
    const value = version;

    return {
      name: 'version',
      operator,
      value,
      type: 'Version',
      displayValue: value,
    };
  },
  releasePatch: (patch, os) => {
    const operator = os === 'Windows' ? 'eq' : '$regex';
    const value = patch;

    return {
      name: 'osVersionDetail.patch',
      operator,
      value,
      type: 'Patch',
      displayValue: value,
    };
  },
  releaseRevision: (revision) => {
    const operator = 'eq';
    const value = revision;

    return {
      name: 'osVersionDetail.revision',
      operator,
      value,
      type: 'Revision',
      displayValue: value,
    };
  },
  secureTokenRemediationFilters: () => ([
    SystemFilters.hasServiceAccount(false, LogicalOperators.or),
    SystemFilters.passwordAPFSValid(false, LogicalOperators.or),
    SystemFilters.passwordODValid(false, LogicalOperators.or),
    SystemFilters.hasSecureToken(false, LogicalOperators.or),
    SystemFilters.createdDaysAgo(10, LogicalOperators.and),
  ]),
  status: bool => ({
    name: 'active',
    operator: 'eq',
    value: bool,
    // The following two props are used to display in filter pill. (type : displayValue)
    type: 'status',
    displayValue: bool ? 'active' : 'inactive',
  }),
  getId: id => ([{
    value: id,
    customType: 'or',
    name: '_id',
    operator: 'eq',
    // to handle case where no index should be added to the filter
    noIndex: true,
  }]),
};

export default SystemFilters;
